import { z } from 'zod'
import {
  rtmpUrlSchema,
  rtmpStreamKeySchema,
} from '@customTypes/cloudmix/common/rtmpStream'
import { srtUrlSchema } from '@customTypes/cloudmix/common/srtStream'
import { mediaInfoSchema } from '@customTypes/cloudmix/common/mediaInfo'
import { connectionInfoSchema } from '@customTypes/cloudmix/common/connectionInfo'
import {
  createFilterableListQuerySchema,
  baseFilterBySchema,
  ORDER_DIRECTIONS,
  paginationSchema,
  timestampsSchema,
} from './api'

export const LIVE_INPUT_TYPES = {
  srt: 'srt',
  rtmp: 'rtmp',
  whip: 'whip',
} as const
export const liveInputTypeSchema = z.nativeEnum(LIVE_INPUT_TYPES)
export type LiveInputType = z.infer<typeof liveInputTypeSchema>

export const LIVE_INPUT_STATUS = {
  connected: 'connected',
  disconnected: 'disconnected',
} as const
export const liveInputStatusSchema = z.nativeEnum(LIVE_INPUT_STATUS)
export type LiveInputStatus = z.infer<typeof liveInputStatusSchema>

export const liveInputIdSchema = z.string()
export type LiveInputId = z.infer<typeof liveInputIdSchema>

const abstractLiveInputDetailsSchema = z.object({
  liveInputId: liveInputIdSchema,
  name: z.string().min(1, 'Name cannot be empty'),
  type: liveInputTypeSchema,
})

export const liveInputRtmpDetailsSchema = abstractLiveInputDetailsSchema.extend(
  {
    type: z.literal(LIVE_INPUT_TYPES.rtmp).default(LIVE_INPUT_TYPES.rtmp),
    url: rtmpUrlSchema,
    streamKey: rtmpStreamKeySchema,
  }
)
export type LiveInputRtmpDetails = z.infer<typeof liveInputRtmpDetailsSchema>

export const liveInputSrtDetailsSchema = abstractLiveInputDetailsSchema.extend({
  type: z.literal(LIVE_INPUT_TYPES.srt).default(LIVE_INPUT_TYPES.srt),
  url: srtUrlSchema,
  streamId: z.string(),
  passphrase: z.string(),
  latency: z.number().min(200).max(10000).default(1000),
})
export type LiveInputSrtDetails = z.infer<typeof liveInputSrtDetailsSchema>

export const liveInputWhipDetailsSchema = abstractLiveInputDetailsSchema.extend(
  {
    type: z.literal(LIVE_INPUT_TYPES.whip).default(LIVE_INPUT_TYPES.whip),
    url: z.string().url(),
  }
)
export type LiveInputWhipDetails = z.infer<typeof liveInputWhipDetailsSchema>

export const liveInputProtocolDetails = z.discriminatedUnion('type', [
  liveInputRtmpDetailsSchema,
  liveInputSrtDetailsSchema,
  liveInputWhipDetailsSchema,
])

const liveInputDetailsSchema = z
  .object({
    liveInputId: liveInputIdSchema,
    name: z.string().min(1, 'Name cannot be empty'),
    protocols: z.array(liveInputProtocolDetails),
    status: liveInputStatusSchema,
    mediaInfo: mediaInfoSchema.optional(),
    connectionInfo: connectionInfoSchema.optional(),
  })
  .and(timestampsSchema)
export type LiveInputDetails = z.infer<typeof liveInputDetailsSchema>

const listLiveInputsResponseSchema = z
  .object({
    items: z.array(liveInputDetailsSchema),
  })
  .and(paginationSchema)
export type ListLiveInputsResponse = z.infer<
  typeof listLiveInputsResponseSchema
>

export const LIVE_INPUT_ORDER_BY_FIELDS = [
  'name',
  'status',
  'createdAt',
] as const

export const DEFAULT_LIST_ORDER = {
  field: 'name',
  direction: ORDER_DIRECTIONS.ascending,
}

export const listLiveInputsRequestSchema = createFilterableListQuerySchema(
  [...LIVE_INPUT_ORDER_BY_FIELDS],
  baseFilterBySchema,
  {
    orderBy: [DEFAULT_LIST_ORDER],
  }
)
export type ListLiveInputsRequest = z.infer<typeof listLiveInputsRequestSchema>

const createLiveInputSchema = z.object({
  name: z.string().min(1, 'Name cannot be empty'),
})
export type CreateLiveInput = z.infer<typeof createLiveInputSchema>

export const createLiveInputResponseSchema = liveInputDetailsSchema
export type CreateLiveInputResponse = z.infer<
  typeof createLiveInputResponseSchema
>

export const updateLiveInputSchema = createLiveInputSchema.extend({
  liveInputId: liveInputIdSchema,
})
export type UpdateLiveInput = z.infer<typeof updateLiveInputSchema>
